import { OrganizationsService } from "@/client"
import type { UserPublic } from "@/client"
import { useQuery, useQueryClient } from "@tanstack/react-query"

// Default Quiz Collective branding
const DEFAULT_BRANDING = {
  display_name: "Quiz Collective",
  logo_url: "/assets/images/qc-logo2.png",
}

interface Branding {
  display_name: string
  logo_url: string
  isLoading: boolean
  isError: boolean
}

/**
 * Smart branding hook with 4-source fallback chain.
 *
 * Sources (in order):
 * 1. currentUser query cache (selected_org_id)
 * 2. localStorage (selected_org_id)
 * 3. URL params (app_id)
 * 4. JWT token (org_id)
 * 5. Default fallback (Quiz Collective)
 */
export function useBranding(): Branding {
  const queryClient = useQueryClient()

  // Determine org_id from multiple sources
  const orgId = determineOrgId(queryClient)

  // Fetch branding from API if we have org_id
  const { data, isLoading, isError } = useQuery({
    queryKey: ["branding", orgId],
    queryFn: () =>
      OrganizationsService.getOrganizationBranding({ identifier: orgId! }),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 60, // 1 hour (branding rarely changes)
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  })

  // Return with fallback to defaults
  if (!orgId || isError) {
    return { ...DEFAULT_BRANDING, isLoading: false, isError: false }
  }

  if (isLoading || !data) {
    return { ...DEFAULT_BRANDING, isLoading: true, isError: false }
  }

  return {
    display_name: data.display_name,
    logo_url: data.logo_url || DEFAULT_BRANDING.logo_url,
    isLoading: false,
    isError: false,
  }
}

/**
 * Determine org_id from multiple sources with fallback chain.
 */
export function determineOrgId(
  queryClient: ReturnType<typeof useQueryClient>,
): string | null {
  // Source 1: Check currentUser query cache
  const currentUser = queryClient.getQueryData<
    UserPublic & { selected_org_id?: string }
  >(["currentUser"])
  if (currentUser?.selected_org_id) {
    return currentUser.selected_org_id
  }

  // Source 2: Check localStorage for org context
  const storedOrgId = localStorage.getItem("selected_org_id")
  if (storedOrgId) {
    return storedOrgId
  }

  // Source 3: Check URL params (embed flow)
  const urlParams = new URLSearchParams(window.location.search)
  const appId = urlParams.get("app_id")
  if (appId) {
    return appId // Can use app_id for branding endpoint
  }

  // Source 4: Extract from JWT token (embed flow)
  const token = localStorage.getItem("access_token")
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]))
      if (payload.org_id) {
        return payload.org_id
      }
    } catch {
      // Invalid token, ignore
    }
  }

  // No org context found - will use default branding
  return null
}
